import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    TextField,
    Button,
    Grid,
    CircularProgress,
    Alert,
    IconButton,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import { ClientInfo, BillingInfo, ClientLinks } from '../../models/ClientInfo';
import { getClient, updateClient } from '../../services/clientService';
import ClientOrders from './components/ClientOrders';
import ClientPayments from './components/ClientPayments';

const ClientProfile: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [client, setClient] = useState<ClientInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchClient();
    }, [id]);

    const fetchClient = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const fetchedClient = await getClient(id);
            if (fetchedClient) {
                setClient(fetchedClient);
                setError(null);
            } else {
                setError('Client not found');
            }
        } catch (err) {
            setError('Failed to fetch client');
            console.error('Error fetching client:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!client || !id) return;
        try {
            setSaving(true);
            await updateClient(id, client);
            setError(null);
        } catch (err) {
            setError('Failed to update client');
            console.error('Error updating client:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field: keyof ClientInfo, value: any) => {
        if (!client) return;
        setClient({ ...client, [field]: value });
    };

    const handleBillingChange = (field: keyof BillingInfo, value: any) => {
        if (!client) return;
        setClient({
            ...client,
            billing: { ...client.billing, [field]: value }
        });
    };

    const handleLinksChange = (field: keyof ClientLinks, value: string) => {
        if (!client) return;
        setClient({
            ...client,
            links: { ...client.links, [field]: value }
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !client) {
        return (
            <Alert severity="error" action={
                <IconButton
                    aria-label="back"
                    color="inherit"
                    size="small"
                    onClick={() => navigate('/clients')}
                >
                    <ArrowBackIcon />
                </IconButton>
            }>
                {error || 'Client not found'}
            </Alert>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={() => navigate('/clients')} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1">
                    {client.companyName}
                </Typography>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Company Name"
                            value={client.companyName}
                            onChange={(e) => handleChange('companyName', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Primary Contact"
                            value={client.primaryContact}
                            onChange={(e) => handleChange('primaryContact', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="CRM Entry ID"
                            value={client.crmEntryId}
                            onChange={(e) => handleChange('crmEntryId', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Notes"
                            value={client.notes}
                            onChange={(e) => handleChange('notes', e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Billing Information</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={client.billing.recurring}
                                    onChange={(e) => handleBillingChange('recurring', e.target.checked)}
                                />
                            }
                            label="Recurring Payment"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Payment Method"
                            value={client.billing.paymentMethod || ''}
                            onChange={(e) => handleBillingChange('paymentMethod', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Billing Address"
                            value={client.billing.billingAddress || ''}
                            onChange={(e) => handleBillingChange('billingAddress', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Next Payment Date"
                            value={client.billing.nextPaymentDate?.toISOString().split('T')[0] || ''}
                            onChange={(e) => handleBillingChange('nextPaymentDate', new Date(e.target.value))}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Links & Access</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="GitHub Repository"
                            value={client.links.githubRepo || ''}
                            onChange={(e) => handleLinksChange('githubRepo', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Database URL"
                            value={client.links.databaseUrl || ''}
                            onChange={(e) => handleLinksChange('databaseUrl', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Cloud Provider"
                            value={client.links.cloudProvider || ''}
                            onChange={(e) => handleLinksChange('cloudProvider', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Email Server"
                            value={client.links.emailServer || ''}
                            onChange={(e) => handleLinksChange('emailServer', e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Other Links"
                            value={client.links.other || ''}
                            onChange={(e) => handleLinksChange('other', e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                    variant="outlined"
                    onClick={() => navigate('/clients')}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </Box>

            {client && (
                <>
                    <ClientOrders clientId={client.id} />
                    <ClientPayments clientId={client.id} />
                </>
            )}
        </Container>
    );
};

export default ClientProfile; 